import {clsx} from 'clsx'
import {
  useFloating,
  autoUpdate,
  offset,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  type Placement,
  autoPlacement,
  useMergeRefs,
} from '@floating-ui/react'
import {useState, useRef, type ReactElement, cloneElement} from 'react'

import styles from './Tooltip.module.css'

interface TooltipProps {
  text: string
  position?: Placement
  children: ReactElement
  relationship?: 'label' | 'description'
  offset?: number
  ref?: React.Ref<HTMLElement>
}

export const Tooltip = ({
  ref,
  text,
  position,
  children,
  relationship = 'description',
  offset: offsetValue = 8,
  ...childrenProps
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const openTimeoutRef = useRef<number | null>(null)

  const {refs, floatingStyles, context, isPositioned} = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: position,
    middleware: [offset(offsetValue), ...(position ? [] : [autoPlacement()])],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    move: false,
    delay: {open: 100, close: 0},
  })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, {
    role: 'tooltip',
    enabled: relationship === 'description',
  })

  const {getReferenceProps, getFloatingProps} = useInteractions([hover, focus, dismiss, role])

  const referenceProps = getReferenceProps()
  const childProps: Record<string, unknown> = childrenProps

  if (relationship === 'label') {
    childProps['aria-label'] = text
  } else if (relationship === 'description') {
    const childDescription =
      typeof children.props === 'object' &&
      children.props !== null &&
      'aria-describedby' in children.props &&
      typeof children.props['aria-describedby'] === 'string'
        ? children.props['aria-describedby']
        : undefined
    childProps['aria-describedby'] = [context.floatingId, childDescription].filter(Boolean).join(' ')
  }

  const handleTouchEnd = (event: React.TouchEvent) => {
    // @ts-expect-error React 19 - Props is of type `Unknown` so we cannot validate it further
    if (children.props.onTouchEnd) {
      // @ts-expect-error React 19 - Props is of type `Unknown` so we cannot validate it further
      children.props.onTouchEnd(event)
    }

    setTimeout(() => setIsOpen(false), 10)
  }

  const handleMouseOver = (event: React.MouseEvent) => {
    // @ts-expect-error React 19 - Props is of type `Unknown` so we cannot validate it further
    if (children.props.onMouseEnter) {
      // @ts-expect-error React 19 - Props is of type `Unknown` so we cannot validate it further
      children.props.onMouseEnter(event)
    }

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }

  const childRef = useMergeRefs([refs.setReference, ref])

  // eslint-disable-next-line @eslint-react/no-clone-element, react-hooks/refs
  const clonedChild = cloneElement(children, {
    ...referenceProps,
    ...childProps,
    // @ts-expect-error React 19 - Props is of type `Unknown` so we cannot validate it further
    onTouchEnd: handleTouchEnd,
    onMouseEnter: handleMouseOver,
    ref: childRef,
  })

  return (
    <>
      {clonedChild}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            id={context.floatingId}
            style={{
              ...floatingStyles,
              visibility: isPositioned ? 'visible' : 'hidden',
            }}
            className={clsx(styles.tooltip)}
            {...getFloatingProps()}
          >
            {text}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

Tooltip.displayName = 'Tooltip'
